import { ErrorModal, getMomentTz } from "@dexteel/mesf-core";
import { Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	type DialogProps,
	Grid,
	
	MenuItem,
	MenuList,
	Paper,
	Snackbar,
	Typography } from '@mui/material';
import { type SyntheticEvent, useCallback, useEffect, useState } from "react";
import { useSearchAssetResources } from "../hooks/useSearchAssetResources";
import { useWorkOrderModalsContext } from "../../context/work-order-modals.context";
import { Alert } from "@mui/lab";
import { WorkOrderAttachmentsTable } from "./resources-tables/WorkOrderAttachments/WorkOrderAttachmentsTable";
import { useSearchWorkOrderAttachments } from "./resources-tables/WorkOrderAttachments/hooks/useSearchWorkOrderAttachments";
import { AssetAttachmentPicker } from "./resources-tables/WorkOrderAttachments/components/asset-Attachments/asset-Attachments-picker";
import { ImportAssetResourcesDialog } from "./resources-tables/asset-resources-dialogs/import-asset-resources-dialog";
import { getCrafts } from "@/pages/task-list/repositorie/TaskListRepositorie";
import { get } from "lodash-es";
import type { Craft } from "../../models/Craft";
import { useSearchWorkOrderResources } from "../hooks/useSearchWorkOrderResources";
import Grid2 from '@mui/material/Grid2';
import { makeStyles } from '@mui/styles';
import type { Theme } from '@mui/material/styles';

const moment = getMomentTz();

const useStyles = makeStyles((theme: Theme) => ({
	root: {
		height: "100%",
	},
	resourcesList: {
		height: "100%",
		display: "flex",
		flexDirection: "column",
		overflow: "hidden",
	},
	menuList: {
		flexGrow: 1,
		overflowY: "auto",
		padding: 0,
	},
	menuItem: {
		borderBottom: `1px solid ${theme.palette.divider}`,
		minWidth: "250px",
	},
	menuItemSelected: {
		backgroundColor: "rgba(173, 216, 230, 0.2)",
	},
	menuItemContent: {
		width: "100%",
		padding: theme.spacing(2),
	},
	tableContainer: {
		height: "100%",
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
	},
	drawingsMenuItem: {
		backgroundColor: "rgba(147, 112, 219, 0.1)",
	},
	attachmentsMenuItem: {
		backgroundColor: "rgba(255, 165, 0, 0.1)",
	},
	lotoMenuItem: {
		backgroundColor: "rgba(50, 205, 50, 0.1)",
	},
	partsMenuItem: {
		backgroundColor: "rgba(70, 130, 180, 0.1)",
	},
}));

export type ResourceType = "ATTACHMENTS";

type Props = {
	show: boolean;
	onHide: (onHide: boolean) => void;
	AssetId: number | null;
	WorkOrderId: number | null;
	newMode?: boolean;
	initialResource?: ResourceType;
	WorkOrderName: string;
	Title: string;
	AssetName: string;
	TypeCode: string;
	CraftId?: number;
	OpenTimestamp?: Date;
	CloseTimestamp?: Date;
};

export const TableWorkOrdesResoures = ({
	show,
	onHide,
	AssetId,
	WorkOrderId,
	newMode,
	initialResource,
	Title,
	WorkOrderName,
	AssetName,
	TypeCode,
	CraftId,
	OpenTimestamp,
	CloseTimestamp,
}: Props) => {
	const [maxWidth, setMaxWidth] = useState<DialogProps["maxWidth"]>("xl");
	const classes = useStyles();

	const {
		state: {
			assetAttachments,
			workOrderAttachmentsList,
		},
		actions: {
			setAssetAttachmentsList,
			setWorkOrderAttachmentsList,
		},
	} = useWorkOrderModalsContext();

	const [isWorkOrderAttachmentsLoading, setIsWorkOrderAttachmentsLoading] =
		useState<boolean>(false);
	const [workOrderAttachmentsError, setWorkOrderAttachmentsError] =
		useState<string>("");
	const [isLoadingResourcesList, setIsLoadingResourcesList] =
		useState<boolean>(false);
	const [errorResourcesList, setErrorResourcesList] = useState<string>("");
	const [assetResourcesMessage, setAssetResourcesMessage] = useState(false);
	const [craftName, setCraftName] = useState<string>("");

	const [selectedItem, setSelectedItem] = useState<string | null>(
		initialResource ? initialResource : "ATTACHMENTS",
	);

	const [showWorkOrderAttachmentsTable, setShowWorkOrderAttachmentsTable] =
		useState(false);
	const [showAssetAttachmentsPicker, setShowAssetAttachmentsPicker] =
		useState(false);
	const [isLoadingWorkOrderResourcesList, setIsLoadingWorkOrderResourcesList] =
		useState<boolean>(false);
	const [errorWorkOrderResourcesList, setErrorWorkOrderResourcesList] =
		useState<string>("");
	const [showImportDialog, setShowImportDialog] = useState(false);

	const getCraftName = useCallback(async (craftId: number) => {
		const res = await getCrafts();
		if (res.ok) {
			const craftsData = get(res, "data.tables[0].rows", []);
			const craft = craftsData.find((c: Craft) => c.CraftId === craftId);
			if (craft) {
				setCraftName(craft.CraftName);
			}
		}
	}, []);

	const searchWorkOrderAttachments = useSearchWorkOrderAttachments({
		WorkOrderId,
		setIsWorkOrderAttachmentsLoading,
		setWorkOrderAttachmentsError,
	});

	const searchAssetResources = useSearchAssetResources({
		AssetId,
		setAssetAttachmentsList,
		setErrorResourcesList,
		setIsLoadingResourcesList,
		setAssetResourcesMessage,
	});

	const searchWorkOrderResources = useSearchWorkOrderResources({
		WorkOrderId,
		setWorkOrderAttachmentsList,
		setErrorWorkOrderResourcesList,
		setIsLoadingWorkOrderResourcesList,
	});

	const handleCloseResourcesMessage = (
		event: Event | React.SyntheticEvent,
		reason?: string,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setAssetResourcesMessage(false);
	};

	const handleCancel = () => {
		onHide(true);
		setWorkOrderAttachmentsList([]);
	};

	const handleItemClick = (item: string) => {
		setSelectedItem(item);
		setShowWorkOrderAttachmentsTable(item === "ATTACHMENTS");
	};

	const showPicker = (item: string) => {
		if (item === "ATTACHMENTS") {
			setShowAssetAttachmentsPicker(true);
		}
	};

	const isButtonDisabled = () => {
		return assetAttachments.length === 0;
	};

	useEffect(() => {
		if (show) {
			searchAssetResources();
			searchWorkOrderResources();
			setSelectedItem("ATTACHMENTS");
			setShowWorkOrderAttachmentsTable(true);
		} else {
			setSelectedItem("ATTACHMENTS");
			setShowWorkOrderAttachmentsTable(false);
			handleCancel();
		}
	}, [show, initialResource]);

	useEffect(() => {
		if (CraftId && show) {
			getCraftName(CraftId);
		}
	}, [CraftId, show, getCraftName]);

	useEffect(() => {
		if (show && newMode) {
			setShowImportDialog(true);
		}
	}, [show, newMode]);

	return (
		<>
			<Dialog
				maxWidth={maxWidth}
				open={show}
				onClose={handleCancel}
				fullWidth={true}
				style={{ margin: "0", padding: 5, width: "100% !important" }}
				className={classes.root}>
				<Grid2 container
					justifyContent="center"
					style={{
						height: "100vh",
						paddingBottom: 0,
						display: "flex",
						alignItems: "center",
					}}>
					<Grid2 size={{ xs: 12, md: 12 }} style={{ padding: "0 30px", height: "auto", minHeight: "8vh", }}>
						<Typography
							variant="h5"
							style={{
								fontWeight: 600,
								paddingTop: 10,
							}}>
							WORK ORDER RESOURCES
						</Typography>

						<Typography
							style={{
								display: "flex",
								alignItems: "center",
								fontSize: "1.3rem",
								flexWrap: "wrap",
								gap: "8px",
								padding: "8px 0 0",
							}}>
							<div
								style={{
									display: "inline-flex",
									alignItems: "center",
									whiteSpace: "nowrap",
									margin: "4px 0",
								}}>
								<span style={{ fontWeight: 600 }}>Work Order:</span>
								<span style={{ marginLeft: "4px" }}>{WorkOrderName}</span>
							</div>

							<div
								style={{
									display: "inline-flex",
									alignItems: "center",
									whiteSpace: "nowrap",
									margin: "4px 8px",
								}}>
								|
							</div>

							<div
								style={{
									display: "inline-flex",
									alignItems: "center",
									whiteSpace: "nowrap",
									margin: "4px 0",
								}}>
								<span style={{ fontWeight: 600 }}>Title:</span>
								<span style={{ marginLeft: "4px" }}>{Title}</span>
							</div>

							<div
								style={{
									display: "inline-flex",
									alignItems: "center",
									whiteSpace: "nowrap",
									margin: "4px 8px",
								}}>
								|
							</div>

							<div
								style={{
									display: "inline-flex",
									alignItems: "center",
									whiteSpace: "nowrap",
									margin: "4px 0",
								}}>
								<span style={{ fontWeight: 600 }}>Asset:</span>
								<span style={{ marginLeft: "4px" }}>{AssetName}</span>
							</div>

							<div
								style={{
									display: "inline-flex",
									alignItems: "center",
									whiteSpace: "nowrap",
									margin: "4px 8px",
								}}>
								|
							</div>

							<div
								style={{
									display: "inline-flex",
									alignItems: "center",
									whiteSpace: "nowrap",
									margin: "4px 0",
								}}>
								<span style={{ fontWeight: 600 }}>Type:</span>
								<span style={{ marginLeft: "4px" }}>{TypeCode}</span>
							</div>

							<div
								style={{
									display: "inline-flex",
									alignItems: "center",
									whiteSpace: "nowrap",
									margin: "4px 8px",
								}}>
								|
							</div>

							<div
								style={{
									display: "inline-flex",
									alignItems: "center",
									whiteSpace: "nowrap",
									margin: "4px 0",
								}}>
								<span style={{ fontWeight: 600 }}>Craft:</span>
								<span style={{ marginLeft: "4px" }}>{craftName}</span>
							</div>

							<div
								style={{
									display: "inline-flex",
									alignItems: "center",
									whiteSpace: "nowrap",
									margin: "4px 8px",
								}}>
								|
							</div>

							<div
								style={{
									display: "inline-flex",
									alignItems: "center",
									whiteSpace: "nowrap",
									margin: "4px 0",
								}}>
								<span style={{ fontWeight: 600 }}>Opened:</span>
								<span style={{ marginLeft: "4px" }}>
									{moment(OpenTimestamp).format("MM/DD/YYYY HH:mm A")}
								</span>
							</div>

							{CloseTimestamp && (
								<>
									<div
										style={{
											display: "inline-flex",
											alignItems: "center",
											whiteSpace: "nowrap",
											margin: "4px 8px",
										}}>
										|
									</div>
									<div
										style={{
											display: "inline-flex",
											alignItems: "center",
											whiteSpace: "nowrap",
											margin: "4px 0",
										}}>
										<span style={{ fontWeight: 600 }}>Closed:</span>
										<span style={{ marginLeft: "4px" }}>
											{moment(CloseTimestamp).format("MM/DD/YYYY HH:mm A")}
										</span>
									</div>
								</>
							)}
						</Typography>
					</Grid2>

					<DialogContent
						dividers
						style={{ height: "66vh", paddingTop: "20px", overflowY: "hidden" }}>
						<Grid2 container
							justifyContent="space-between"
							alignItems="stretch"
							className={classes.root}
							style={{ height: "100%" }}>
							<Grid2 size={{ xs: 12, md: 3 }} style={{ height: "100%" }}>
								<Paper elevation={1} className={classes.resourcesList}>
									<MenuList className={`mes-submenu ${classes.menuList}`}>
										<MenuItem
											className={`${classes.menuItem} ${
												classes.attachmentsMenuItem
											} ${
												selectedItem === "ATTACHMENTS"
													? classes.menuItemSelected
													: ""
											}`}
											onClick={() => handleItemClick("ATTACHMENTS")}>
											<div className={classes.menuItemContent}>
												<Typography variant="h6">
													ATTACHMENTS
													{isLoadingResourcesList ? (
														<CircularProgress
															size={20}
															style={{
																marginLeft: "8px",
																verticalAlign: "middle",
															}} />
													) : (
														workOrderAttachmentsList.length> 0 &&
														` (${workOrderAttachmentsList.length})`
													)}
												</Typography>
											</div>
										</MenuItem>
									</MenuList>
								</Paper>
							</Grid2>

							<Grid2 size={{ xs: 12, md: 9 }} style={{ height: "100%" }}>
								<Paper elevation={1} className={classes.tableContainer}>
									<WorkOrderAttachmentsTable
										AssetId={AssetId}
										newMode={newMode}
										WorkOrderId={WorkOrderId}
										searchAssetResources={searchAssetResources}
										show={showWorkOrderAttachmentsTable}
										onHide={() => {
											setShowWorkOrderAttachmentsTable(false);
										}}
										setShowAssetAttachmentsPicker={
											setShowAssetAttachmentsPicker
										}
										isLoadingResourcesList={isLoadingResourcesList}
										WorkOrderName={WorkOrderName}
										AssetName={AssetName} />
								</Paper>
							</Grid2>
						</Grid2>
					</DialogContent>

					<Grid2 size={{ xs: 12, md: 12 }}>
						<DialogActions
							sx={{
								padding: "0px 24px",
								marginTop: "16px",
								paddingBottom: "16px",
								display: "flex",
								alignItems: "center",
							}}>
							<Grid2
								container
								spacing={2}
								style={{ margin: 0, display: "flex", alignItems: "center" }}>
								<Grid2 size={{ xs: 4, md: 3 }}>
									<Button
										fullWidth
										variant="contained"
										color="inherit"
										onClick={handleCancel}>
										Close
									</Button>
								</Grid2>
							</Grid2>
							{selectedItem && (
								<Grid2 size={{ xs: 12, md: 4 }} style={{ paddingRight: "8px" }}>
									<Button
										variant="contained"
										color="primary"
										onClick={() => showPicker(selectedItem)}
										fullWidth
										disabled={isButtonDisabled()}
										style={{ minWidth: 300, height: 40 }}>
										{`${selectedItem}`} FROM ASSET
									</Button>
								</Grid2>
							)}
						</DialogActions>
					</Grid2>

					<AssetAttachmentPicker
						show={showAssetAttachmentsPicker}
						WorkOrderId={WorkOrderId}
						onHide={(shouldUpdate) => {
							if (shouldUpdate) {
								searchWorkOrderAttachments();
							}
							setShowAssetAttachmentsPicker(false);
						}}
						WorkOrderName={WorkOrderName}
						AssetName={AssetName}
						Title={Title} />

					<ImportAssetResourcesDialog
						open={showImportDialog}
						onClose={() => setShowImportDialog(false)}
						onOpenAssetAttachmentsPicker={() => {
							setShowAssetAttachmentsPicker(true);
						}} />

					<ErrorModal
						error={errorResourcesList}
						onHide={() => setErrorResourcesList("")} />
					<ErrorModal
						error={workOrderAttachmentsError}
						onHide={() => setWorkOrderAttachmentsError("")} />

					<Snackbar
						open={assetResourcesMessage}
						autoHideDuration={2500}
						onClose={handleCloseResourcesMessage}>
						<Alert severity="warning" onClose={handleCloseResourcesMessage}>
							The asset has no assigned resources
						</Alert>
					</Snackbar>
				</Grid2>
			</Dialog>
		</>
	);
};
